import React, { useState, useRef } from "react";
import { API_BASE_URL } from "../../Common/Common";
import { Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { DateTime } from "luxon";
import { geocode, setKey, RequestType } from "react-geocode";
import { GlobalWorkerOptions, getDocument } from "pdfjs-dist/legacy/build/pdf";
import pdfjsWorker from "pdfjs-dist/legacy/build/pdf.worker.entry";
import { slice1Actions } from "../../store/reducer";
import Place from "../../Models/Place";
import { countries } from "../../Data/Countries";
import { citiesByCountry } from "../../Data/Cities";
import MultiSelectModal from "./MultiSelectModal";
import PromptModal from "./PromptModal";
import "./Modal.css";
import "./ImportConfirmationModal.css";

GlobalWorkerOptions.workerSrc = pdfjsWorker;

const PARSE_CONFIRMATION_URL = `${API_BASE_URL}/parse_confirmation.php`;
const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10MB — mirrors the server-side cap in parse_confirmation.php

// Case-insensitive match against the app's canonical country list
// (src/Data/Countries.js), returning the exact-cased entry from that list
// so a place's Country field always ends up matching what the rest of the
// app expects (map filters, GuideAttributes.countries, etc.).
const resolveCountry = (guess) => {
  const found = countries.find((c) => c.toLowerCase() === String(guess || "").trim().toLowerCase());
  return found || null;
};

// Case-insensitive/trimmed string equality — both null/undefined compare
// equal to each other (e.g. two places with no name) but not to "".
const namesMatch = (a, b) => String(a ?? "").trim().toLowerCase() === String(b ?? "").trim().toLowerCase();

// AI datetimes come back as plain ISO strings, e.g. "2026-09-13T16:00:00" —
// <input type="datetime-local"> wants the same shape minus seconds, so a
// straight substring is enough; no timezone conversion happens here.
const toDatetimeLocal = (iso) => (iso ? iso.slice(0, 16) : "");

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

// A real live test (Sep 2026) found the AI's own PDF-file handling
// occasionally drops/mislabels a row that a plain image of the same content
// never did (14/14 correct across 3 runs as an image vs. 2 real errors
// across 3 runs as a raw PDF) — rendering the page ourselves and sending it
// as an image sidesteps whatever's happening inside OpenAI's PDF pipeline.
// Only safe to do for a single-page PDF, since one image can only carry one
// page — a multi-page PDF still goes through as a raw PDF file (accepted
// tradeoff: multi-page itineraries are expected to be rare).
//
// Kept deliberately modest (not the 2600px/4x-scale this originally shipped
// with) after a real iPhone 7 crashed on this step — a big canvas + its raw
// pixel buffer + the PNG encode + the base64 string, all alive at once, is
// real memory pressure on a 2GB-RAM device, and iOS Safari kills tabs that
// exceed its per-tab budget rather than throwing a catchable error. Printed
// text doesn't need to be huge to stay legible to the model, so this trades
// some resolution for a much smaller peak memory footprint. Not a guarantee
// — an OOM tab-kill can't be caught in JS — just a real reduction in risk.
const SINGLE_PAGE_TARGET_MAX_DIM = 1400;
const SINGLE_PAGE_MAX_SCALE = 2;

const renderSinglePagePdfToImageBase64 = async (file) => {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await getDocument({ data: arrayBuffer }).promise;
  try {
    if (pdf.numPages !== 1) return null;
    const page = await pdf.getPage(1);
    const baseViewport = page.getViewport({ scale: 1 });
    const scale = Math.min(SINGLE_PAGE_MAX_SCALE, SINGLE_PAGE_TARGET_MAX_DIM / Math.max(baseViewport.width, baseViewport.height));
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement("canvas");
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
    // Drop the canvas's backing bitmap immediately rather than waiting for
    // GC — canvases are one of the heavier things to hold onto on iOS Safari.
    canvas.width = 0;
    canvas.height = 0;
    if (!blob) return null;
    return await fileToBase64(blob);
  } finally {
    // Releases pdf.js's internal caches/worker resources for this document
    // right away instead of leaving them until the next GC pass.
    pdf.destroy();
  }
};

// Confirmation-email import v2 (docs/competitive-analysis.md rec #2). Paste
// text OR upload a PDF/photo of a booking confirmation or day-by-day
// itinerary → sent to the AI (the file itself, for OCR/analysis, not
// client-extracted text) → extracts an arbitrary number of stops (not just
// one departure/arrival pair, so multi-port cruises etc. work) → user
// reviews/edits places and events, each individually toggleable → confirmed
// places get geocoded and added to the guide, confirmed events get added to
// the schedule, linked to their place. Place/event creation conventions
// (duplicate detection, plain-object addEvent shape) mirror
// ImportTripModal.js's importData() (:304-382) rather than inventing new ones.
export default function ImportConfirmationModal({ show, onHide, onImported, onOfferTripAI }) {
  const dispatch = useDispatch();
  const ScheduleName = useSelector((state) => state.ScheduleName);
  const user_info = useSelector((state) => state.user_info);
  const eventDurationMinutes =
    typeof user_info?.default_duration === "undefined" ? 30 : Number(user_info.default_duration);

  const [step, setStep] = useState("input"); // "input" | "loading" | "review" | "error" | "offer"
  const [pastedText, setPastedText] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [confirmationNumber, setConfirmationNumber] = useState("");
  const [places, setPlaces] = useState([]); // [{city, country, selected}]
  const [events, setEvents] = useState([]); // [{subject, place_index (nullable), datetime, kind, selected}]
  const [combineArrivalDeparture, setCombineArrivalDeparture] = useState(false);
  const [lastCreatedEvents, setLastCreatedEvents] = useState([]);
  const [confirmStatus, setConfirmStatus] = useState("");
  const abortRef = useRef(null);
  const fileInputRef = useRef(null);

  const [countryPickerShow, setCountryPickerShow] = useState(false);
  const [countryPickerTarget, setCountryPickerTarget] = useState(null); // index into places
  const [cityPickerShow, setCityPickerShow] = useState(false);
  const [cityPickerTarget, setCityPickerTarget] = useState(null); // index into places
  const [cityPromptShow, setCityPromptShow] = useState(false);
  const [cityPromptTarget, setCityPromptTarget] = useState(null); // index into places

  const reset = () => {
    setStep("input");
    setPastedText("");
    setSelectedFile(null);
    setErrorMsg("");
    setConfirmationNumber("");
    setPlaces([]);
    setEvents([]);
    setCombineArrivalDeparture(false);
    setLastCreatedEvents([]);
    if (abortRef.current) abortRef.current.abort();
  };

  const handleClose = () => {
    reset();
    onHide();
  };

  const handleFileChosen = (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > MAX_FILE_BYTES) {
      setErrorMsg("File is too large — please use a file under 10MB.");
      return;
    }
    setErrorMsg("");
    setSelectedFile(file);
  };

  const buildRequestBody = async () => {
    if (selectedFile) {
      if (selectedFile.type === "application/pdf") {
        // Single-page PDFs render to an image first (see
        // renderSinglePagePdfToImageBase64 for why) — a multi-page PDF can't
        // be represented as one image, so it falls through to the raw-PDF
        // path unchanged, same as before this fix.
        try {
          const rendered = await renderSinglePagePdfToImageBase64(selectedFile);
          if (rendered) {
            return { file_base64: rendered, file_name: selectedFile.name.replace(/\.pdf$/i, ".png"), file_mime: "image/png" };
          }
        } catch (e) {
          // Rendering failed for some reason (corrupt/encrypted PDF, etc.) —
          // fall back to sending the raw PDF rather than blocking the import.
        }
      }
      const file_base64 = await fileToBase64(selectedFile);
      return { file_base64, file_name: selectedFile.name, file_mime: selectedFile.type };
    }
    return { text: pastedText };
  };

  const handleExtract = async () => {
    if (pastedText.trim() === "" && !selectedFile) return;
    setStep("loading");
    setErrorMsg("");
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const body = await buildRequestBody();
      const r = await fetch(PARSE_CONFIRMATION_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        signal: controller.signal,
      });
      const res = await r.json();
      if (!res || res.found !== true) {
        setErrorMsg(
          (res && res.reason) ||
            "Couldn't find a clear itinerary in that document. Check the input or add events manually."
        );
        setStep("error");
        return;
      }
      const extractedPlaces = (res.places || []).map((p) => {
        const resolved = resolveCountry(p.country);
        return {
          city: p.city || "",
          country: resolved || p.country || "",
          // A specific named location (currently just hotels/lodging) —
          // geocoded and displayed by name instead of just city, so e.g. two
          // different hotels in the same city don't collapse into one place.
          name: p.name || "",
          countryResolved: resolved !== null,
          selected: resolved !== null,
        };
      });
      const extractedEvents = (res.events || []).map((e) => {
        const placeIndex = e.place_index === undefined ? null : e.place_index;
        return {
          subject: e.subject || "",
          place_index: placeIndex,
          datetime: toDatetimeLocal(e.datetime),
          kind: e.kind || "other",
          allDay: e.all_day === true || e.kind === "day",
          // A place-less "day" event (Sea Day, Scenic Cruising, etc.) has
          // nothing to resolve, so it's selectable by default; a place-linked
          // event only defaults on once its place has a valid country.
          selected: placeIndex === null ? true : extractedPlaces[placeIndex]?.countryResolved === true,
        };
      });
      setConfirmationNumber(res.confirmation_number || "");
      setPlaces(extractedPlaces);
      setEvents(extractedEvents);
      setStep("review");
    } catch (e) {
      if (e.name === "AbortError") return;
      setErrorMsg("Something went wrong reaching the AI service. Please try again.");
      setStep("error");
    }
  };

  const openCountryPicker = (placeIndex) => {
    setCountryPickerTarget(placeIndex);
    setCountryPickerShow(true);
  };

  const countryPickerItems = countries
    .slice()
    .sort()
    .map((c, i) => ({ label: c, name: c, value: c, id: String(i + 1) }));

  const handleCountryPicked = (selected) => {
    if (countryPickerTarget === null || selected.length === 0) return;
    const chosen = selected[0].value;
    const idx = countryPickerTarget;
    setPlaces((prev) =>
      prev.map((p, i) => {
        if (i !== idx) return p;
        // Blanking the city on an actual country change (not a no-op
        // reselect, and not the initial resolve) avoids leaving a stale
        // city from the old country — city choices are scoped to a
        // country. Only applies to the free-text "no name" rows; a named
        // place (e.g. a hotel) keeps its own city as read-only context.
        const cityChanged = !p.name && p.countryResolved && chosen !== p.country;
        return { ...p, country: chosen, countryResolved: true, selected: true, city: cityChanged ? "" : p.city };
      })
    );
    // A place that just got a valid country can now let its events be selected too.
    setEvents((prev) => prev.map((e) => (e.place_index === idx ? { ...e, selected: true } : e)));
    setCountryPickerTarget(null);
  };

  const CITY_PICKER_OTHER = "__other__";

  // Country-first: if the country isn't resolved yet, redirect there
  // instead of showing an empty/meaningless city list. Only used for a
  // "no name" row — a named place (e.g. a hotel) shows its city as
  // read-only context and doesn't go through this at all.
  const openCityPicker = (placeIndex) => {
    const p = places[placeIndex];
    if (!p.countryResolved || !p.country) {
      openCountryPicker(placeIndex);
      return;
    }
    if (!(citiesByCountry[p.country] || []).length) {
      setCityPromptTarget(placeIndex);
      setCityPromptShow(true);
      return;
    }
    setCityPickerTarget(placeIndex);
    setCityPickerShow(true);
  };

  const cityPickerItemsFor = (country) => {
    const cityList = (citiesByCountry[country] || []).slice().sort((a, b) => b.popularity - a.popularity);
    const items = cityList.map((c, i) => ({ label: c.city, name: c.city, value: c.city, id: String(i + 1) }));
    items.push({ label: "Type a different city…", name: "other", value: CITY_PICKER_OTHER, id: String(items.length + 1) });
    return items;
  };

  const handleCityPicked = (selected) => {
    if (cityPickerTarget === null || selected.length === 0) return;
    const idx = cityPickerTarget;
    setCityPickerTarget(null);
    if (selected[0].value === CITY_PICKER_OTHER) {
      setCityPromptTarget(idx);
      setCityPromptShow(true);
      return;
    }
    updatePlaceField(idx, "city", selected[0].value);
  };

  const handleCityTyped = (inputs) => {
    if (cityPromptTarget === null) return;
    const city = (inputs?.[0]?.value || "").trim();
    if (city !== "") updatePlaceField(cityPromptTarget, "city", city);
    setCityPromptTarget(null);
  };

  const togglePlace = (idx) => {
    setPlaces((prev) => prev.map((p, i) => (i === idx ? { ...p, selected: !p.selected } : p)));
  };

  const toggleEvent = (idx) => {
    setEvents((prev) => prev.map((e, i) => (i === idx ? { ...e, selected: !e.selected } : e)));
  };

  const updatePlaceField = (idx, field, value) => {
    setPlaces((prev) => prev.map((p, i) => (i === idx ? { ...p, [field]: value } : p)));
  };

  const updateEventField = (idx, field, value) => {
    setEvents((prev) => prev.map((e, i) => (i === idx ? { ...e, [field]: value } : e)));
  };

  // Named places (e.g. "A-Factory") showed with no city/country context in
  // the Events list, even though that data is already known — the Places
  // list above already shows it, but a duplicate named place in a
  // different (or the same) city was invisible here, indistinguishable
  // from a genuine second visit until Confirm actually ran.
  const eventPlaceLabel = (pl) => {
    if (!pl) return "";
    const location = [pl.city, pl.country].filter(Boolean).join(", ");
    if (!pl.name) return pl.city || "";
    return location ? `${pl.name} (${location})` : pl.name;
  };

  // When combineArrivalDeparture is on, events at the same place pair up
  // chronologically into "merged" display/creation units spanning both
  // times (e.g. Ketchikan arrival 7am + departure 4pm -> one 7am-4pm
  // "Ketchikan" event). This pairs within a place's FULL event list (not
  // just exactly-2 groups), so a place visited twice — e.g. Aomori, with
  // two separate arrival+departure pairs sharing the same place_index —
  // still produces two merged pairs instead of falling through unmerged;
  // an odd one out (embarkation-only departure, disembarkation-only
  // arrival, or a genuinely unpaired stop) stays a single unit. Day events
  // (place_index null) are always single units — nothing to pair with.
  // Shared by both the live review-list preview and the Confirm creation
  // step, via `indices` into the current `events` array, so there's one
  // source of truth for what "merged" means rather than two.
  const buildDisplayUnits = () => {
    if (!combineArrivalDeparture) return events.map((_, i) => ({ kind: "single", indices: [i] }));
    const byPlace = new Map();
    const standaloneIdx = [];
    events.forEach((e, i) => {
      if (e.place_index === null) {
        standaloneIdx.push(i);
        return;
      }
      if (!byPlace.has(e.place_index)) byPlace.set(e.place_index, []);
      byPlace.get(e.place_index).push(i);
    });
    const units = [];
    byPlace.forEach((idxGroup) => {
      const sorted = [...idxGroup].sort((a, b) =>
        events[a].datetime < events[b].datetime ? -1 : events[a].datetime > events[b].datetime ? 1 : 0
      );
      for (let k = 0; k + 1 < sorted.length; k += 2) {
        units.push({ kind: "merged", indices: [sorted[k], sorted[k + 1]] });
      }
      if (sorted.length % 2 === 1) units.push({ kind: "single", indices: [sorted[sorted.length - 1]] });
    });
    standaloneIdx.forEach((i) => units.push({ kind: "single", indices: [i] }));
    units.sort((a, b) => (events[a.indices[0]].datetime < events[b.indices[0]].datetime ? -1 : 1));
    return units;
  };

  const handleConfirm = async () => {
    setKey(process.env.REACT_APP_GOOGLE_MAPS_GEOCODING_KEY);
    const guide = window.guide_obj;
    setErrorMsg("");
    try {
    // Pass 1: resolve every place (checked ones get created/deduped, unchecked
    // ones still try to resolve to an existing match) before touching events,
    // since events need a real place ID to link to. Sequential, not
    // Promise.all — firing every geocode call at once risks a transient
    // failure/rate-limit on one of them, and a parallel failure is easy to
    // swallow silently; sequential calls fail one at a time, so each failure
    // can be tracked and reported instead of just vanishing.
    const placeIds = [];
    const failedPlaceNames = [];
    let placeIndex = 0;
    for (const p of places) {
      placeIndex++;
      setConfirmStatus(`Adding places… (${placeIndex} of ${places.length})`);
      if (!p.countryResolved) {
        placeIds.push(null);
        continue;
      }
      // A named place (e.g. a hotel) needs its own name in the match too —
      // matching on City+Country alone would collapse a specific hotel into
      // an unrelated existing city-level place in the same city. Case-
      // insensitive/trimmed — the same stop parsed twice out of one
      // confirmation (or matched against an earlier import) can come back
      // with slightly different capitalization/whitespace, which an exact
      // === comparison treated as two different places (confirmed:
      // duplicate "A-Factory"/"Aomori Park" entries after import).
      const placeName = p.name || p.city;
      const existing = guide.places.placesList.find(
        (ex) => namesMatch(ex.Name, placeName) && namesMatch(ex.City, p.city) && namesMatch(ex.Country, p.country)
      );
      if (existing) {
        placeIds.push(existing.ID);
        continue;
      }
      if (!p.selected) {
        placeIds.push(null); // not creating, no existing match either
        continue;
      }
      try {
        const geocodeQuery = p.name ? `${p.name}, ${p.city}, ${p.country}` : `${p.city}, ${p.country}`;
        const response = await geocode(RequestType.ADDRESS, geocodeQuery);
        const { lat, lng } = response.results[0].geometry.location;
        // Duplicate check mirrors ImportTripModal.js importData() (:311-316),
        // widened from 0.0001° (~11m) to 0.0005° (~55m) and case-insensitive/
        // trimmed on the name — a tight radius plus exact-string matching
        // let the same real place geocode-and-create twice when a repeat
        // confirmation line item had slightly different capitalization or
        // Google's geocoder returned a marginally different point for the
        // same address on a second lookup.
        const dupe = guide.places.placesList.find(
          (ex) =>
            namesMatch(ex.Name, placeName) &&
            Math.abs(parseFloat(ex.Lat) - lat) < 0.0005 &&
            Math.abs(parseFloat(ex.Longi) - lng) < 0.0005
        );
        if (dupe) {
          placeIds.push(dupe.ID);
        } else {
          const np = new Place(guide.places);
          np.Name = placeName;
          np.City = p.city;
          np.Country = p.country;
          np.Lat = lat;
          np.Longi = lng;
          placeIds.push(guide.places.addPlace(np));
        }
      } catch (err) {
        console.log("ImportConfirmationModal: geocode/create failed for", placeName, p.country, err);
        placeIds.push(null);
        failedPlaceNames.push(placeName);
      }
    }

    {
      // Only units where every underlying event is selected get created —
      // matches what the review list's checkboxes show/control, whether
      // merged or single.
      const unitsToCreate = buildDisplayUnits().filter((u) => u.indices.every((idx) => events[idx].selected));

      let addedEvents = 0;
      const createdEvents = [];
      unitsToCreate.forEach((u) => {
        const isMerged = u.kind === "merged";
        const first = events[u.indices[0]];
        const second = isMerged ? events[u.indices[1]] : null;
        if (!first.datetime) return;
        const start = DateTime.fromISO(first.datetime, { zone: "utc" });
        if (!start.isValid) return;
        let end = start.plus({ minutes: eventDurationMinutes });
        if (isMerged) {
          const endDt = DateTime.fromISO(second.datetime, { zone: "utc" });
          if (endDt.isValid) end = endDt;
        } else if (first.allDay) {
          // No specific time given — span the whole day rather than the
          // default event duration.
          end = start.set({ hour: 23, minute: 59, second: 0, millisecond: 0 });
        }
        const mergedPlace = places[first.place_index];
        const subject = isMerged ? mergedPlace?.name || mergedPlace?.city || first.subject : first.subject;
        const isDuplicate = guide.schedule.events.some(
          (ex) =>
            ex.Subject === subject &&
            ex.Start?.toISO?.() === start.toISO() &&
            (ex.Itinerary || "Main") === ScheduleName
        );
        if (isDuplicate) return;
        const newEvent = guide.schedule.addEvent({
          Subject: subject,
          Start: start,
          End: end,
          Places: first.place_index !== null ? placeIds[first.place_index] || "" : "",
          Itinerary: ScheduleName,
          Info: confirmationNumber ? `Confirmation #: ${confirmationNumber}` : "",
        });
        createdEvents.push(newEvent);
        addedEvents++;
      });
      if (addedEvents === 0 && failedPlaceNames.length === 0) {
        setErrorMsg("Nothing was created — check that at least one event is selected and has a valid date/time.");
        return;
      }
      if (onImported) {
        // Opened embedded from TripAI's "Import Confirmation Email" link —
        // the caller owns navigation/next-steps (it re-derives destinations
        // from just the events created here, not the whole current guide's
        // schedule — TripAI may be building "A new guide," where the rest of
        // window.guide_obj's schedule is unrelated leftover content from
        // whatever guide happens to be open), so skip the Map jump below.
        onImported(createdEvents);
        return;
      }
      // Jump to the Map and fit bounds to the newly-created places, same
      // "GuidesOrPlaces" + "CurrentAppScreen" navigation MobileBottomNavBar.js
      // itself uses (navigateAndOpenMenu), plus fitToBounds mirroring
      // VerticalSidebar.js's updateGuide().
      //
      // In mobile mode, <Map> only mounts once CurrentAppScreen === "Map" (it's
      // not always-rendered like in desktop's side-by-side layout), and the
      // reducer's fitToBounds handling silently no-ops unless window.mapRef
      // already exists (store/reducer.js ~line 556) — so dispatching the
      // screen-switch and fitToBounds in the same synchronous batch races
      // Map's mount and loses the fit. Same setTimeout(…, 350) delay
      // navigateAndOpenMenu already uses for exactly this reason.
      dispatch(slice1Actions.setGuidesOrPlaces({ value: "Places", GuideType: "None" }));
      dispatch(slice1Actions.setCurrentAppScreen({ value: "Map" }));
      setTimeout(() => {
        dispatch(slice1Actions.setGuide({ guide: null, fitToBounds: true, update_guide_attributes: true }));
      }, 350);
      if (failedPlaceNames.length > 0) {
        // Don't silently close over a partial failure — whatever succeeded is
        // already created/dispatched above, but the user needs to know some
        // places didn't geocode so they can retry (Confirm again — already-
        // created places are deduped by Name+Lat/Longi, so retrying is safe).
        // Still offer the TripAI link below if available (a pasted email
        // commonly has one imperfect city name among several good ones —
        // that shouldn't block the offer for everything else that worked).
        setErrorMsg(
          `Couldn't geocode: ${failedPlaceNames.join(", ")}. Everything else was created — try Confirm again to retry just those.`
        );
        if (!onOfferTripAI) return;
      }
      if (onOfferTripAI) {
        setLastCreatedEvents(createdEvents);
        setStep("offer");
        return;
      }
      handleClose();
    }
    } finally {
      setConfirmStatus("");
    }
  };

  const renderInput = () => (
    <>
      <p style={{ fontSize: "0.9rem", color: "#666" }}>
        Paste a booking confirmation or day-by-day itinerary below, or upload a PDF/photo of one.
        The AI will pull out every stop and event so you can review them before adding them to
        your guide.
      </p>
      <textarea
        className="form-control"
        rows={8}
        placeholder="Paste your confirmation email or itinerary text here…"
        value={pastedText}
        onChange={(e) => {
          setPastedText(e.target.value);
          if (e.target.value) setSelectedFile(null);
        }}
        disabled={!!selectedFile}
        autoFocus
      />
      <div style={{ margin: "0.75rem 0", textAlign: "center", color: "#999", fontSize: "0.8rem" }}>— or —</div>
      <input
        type="file"
        accept="application/pdf,image/*"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFileChosen}
      />
      <button
        className="btn btn-outline-secondary w-100"
        onClick={() => fileInputRef.current?.click()}
        disabled={!!pastedText.trim()}
      >
        {selectedFile ? `Selected: ${selectedFile.name}` : "Upload PDF or photo…"}
      </button>
      {selectedFile && (
        <button
          className="btn btn-link btn-sm"
          onClick={() => setSelectedFile(null)}
          style={{ display: "block", margin: "0.25rem auto 0" }}
        >
          Remove file
        </button>
      )}
      {errorMsg && <p style={{ color: "#b00020", marginTop: "0.5rem" }}>{errorMsg}</p>}
    </>
  );

  const renderLoading = () => (
    <div style={{ textAlign: "center", padding: "2rem 0" }}>
      <p>Reading your itinerary…</p>
    </div>
  );

  const renderError = () => (
    <div>
      <p style={{ color: "#b00020" }}>{errorMsg}</p>
      <button className="btn btn-secondary btn-sm" onClick={() => setStep("input")}>
        Back
      </button>
    </div>
  );

  const renderOffer = () => (
    <div style={{ textAlign: "center", padding: "1rem 0" }}>
      {errorMsg && <p style={{ color: "#b00020" }}>{errorMsg}</p>}
      <p>Places and events were added to your guide.</p>
      <p style={{ fontWeight: 600 }}>Generate an AI-enhanced trip from these destinations?</p>
      <p style={{ fontSize: "0.85rem", color: "#666" }}>
        AI can suggest things to do, fill in places to visit, and build out your schedule for these stops.
      </p>
    </div>
  );

  const renderReview = () => (
    <div>
      {errorMsg && <p style={{ color: "#b00020" }}>{errorMsg}</p>}

      <p style={{ fontSize: "0.85rem", color: "#666", marginBottom: "0.75rem" }}>
        Everything checked below will be added. Uncheck anything you don't want — e.g. a "Sea Day" or
        other place-less event is checked by default but doesn't need its own place.
      </p>

      <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>Places</p>
      {places.length === 0 && <p style={{ fontSize: "0.85rem", color: "#999" }}>No places found.</p>}
      {places.map((p, i) => (
        <div key={i} className="ic-place-row">
          <input
            type="checkbox"
            className="ic-row-checkbox"
            checked={p.selected}
            disabled={!p.countryResolved}
            onChange={() => togglePlace(i)}
          />
          <div className="ic-place-name-col">
            {p.name ? (
              <>
                <input
                  className="form-control"
                  value={p.name}
                  onChange={(e) => updatePlaceField(i, "name", e.target.value)}
                />
                {/* A named place (e.g. a hotel) edits its own name above, with
                    the city it belongs to shown as read-only context
                    underneath — the city field itself isn't directly
                    editable here since changing it wouldn't also move the
                    hotel's geocode query. */}
                <span className="ic-place-city-hint">{p.city}</span>
              </>
            ) : (
              // No distinct name — picks from the resolved country's curated
              // city list, with a "type a different city" fallback.
              <button className="form-control text-start" onClick={() => openCityPicker(i)}>
                {p.city || "Select a city…"}
              </button>
            )}
          </div>
          {p.countryResolved ? (
            <button className="btn btn-outline-secondary btn-sm ic-country-btn" onClick={() => openCountryPicker(i)}>
              {p.country}
            </button>
          ) : (
            <button className="btn btn-outline-danger btn-sm ic-country-btn" onClick={() => openCountryPicker(i)}>
              ⚠ Select country
            </button>
          )}
        </div>
      ))}

      <p style={{ fontWeight: 600, marginTop: "1rem", marginBottom: "0.25rem" }}>Events</p>
      <label className="ic-combine-row">
        <input
          type="checkbox"
          checked={combineArrivalDeparture}
          onChange={(e) => setCombineArrivalDeparture(e.target.checked)}
        />
        Combine arrival &amp; departure into one event per place (e.g. "Busan 8:00am–7:00pm")
      </label>
      {events.length === 0 && <p style={{ fontSize: "0.85rem", color: "#999" }}>No events found.</p>}
      {buildDisplayUnits().map((u, ui) => {
        if (u.kind === "single") {
          const i = u.indices[0];
          const e = events[i];
          return (
            <div key={ui} className="ic-event-row">
              <input
                type="checkbox"
                className="ic-row-checkbox"
                checked={e.selected}
                disabled={e.place_index !== null && !places[e.place_index]?.countryResolved}
                onChange={() => toggleEvent(i)}
              />
              <input
                className="form-control"
                value={e.subject}
                onChange={(ev) => updateEventField(i, "subject", ev.target.value)}
              />
              <span className="ic-place-label" title={e.place_index !== null ? eventPlaceLabel(places[e.place_index]) : ""}>
                {e.place_index !== null
                  ? eventPlaceLabel(places[e.place_index])
                  : <span className="ic-kind-badge">Day</span>}
              </span>
              <input
                type="datetime-local"
                className="form-control"
                value={e.datetime}
                onChange={(ev) => updateEventField(i, "datetime", ev.target.value)}
              />
            </div>
          );
        }
        // Merged preview — a place visited with two events (e.g. arrival +
        // departure) collapses into one row with both times shown, live,
        // matching exactly what Confirm will actually create.
        const [i, j] = u.indices;
        const first = events[i];
        const second = events[j];
        const checked = first.selected && second.selected;
        return (
          <div key={ui} className="ic-event-row ic-event-row-merged">
            <input
              type="checkbox"
              className="ic-row-checkbox"
              checked={checked}
              disabled={!places[first.place_index]?.countryResolved}
              onChange={() => {
                const newVal = !checked;
                setEvents((prev) => prev.map((e, idx) => (idx === i || idx === j ? { ...e, selected: newVal } : e)));
              }}
            />
            <input
              className="form-control"
              value={first.subject}
              onChange={(ev) => updateEventField(i, "subject", ev.target.value)}
            />
            <span className="ic-place-label" title={eventPlaceLabel(places[first.place_index])}>{eventPlaceLabel(places[first.place_index])}</span>
            <input
              type="datetime-local"
              className="form-control"
              value={first.datetime}
              onChange={(ev) => updateEventField(i, "datetime", ev.target.value)}
            />
            <input
              type="datetime-local"
              className="form-control"
              value={second.datetime}
              onChange={(ev) => updateEventField(j, "datetime", ev.target.value)}
            />
          </div>
        );
      })}

      <div style={{ marginTop: "1rem" }}>
        <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>Confirmation number / notes</p>
        <input
          className="form-control"
          value={confirmationNumber}
          onChange={(e) => setConfirmationNumber(e.target.value)}
        />
      </div>
    </div>
  );

  return (
    <>
      <Modal show={show} onHide={handleClose} centered dialogClassName="icm-dialog" size={step === "review" ? "lg" : undefined}>
        <Modal.Header closeButton>
          <Modal.Title>Import Confirmation / Itinerary</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "1rem", overflowX: "auto" }}>
          {step === "input" && renderInput()}
          {step === "loading" && renderLoading()}
          {step === "error" && renderError()}
          {step === "review" && renderReview()}
          {step === "offer" && renderOffer()}
        </Modal.Body>
        <Modal.Footer>
          {step !== "offer" && (
            <button className="btn btn-secondary btn-sm" onClick={handleClose}>
              Cancel
            </button>
          )}
          {step === "input" && (
            <button
              className="btn btn-primary btn-sm"
              onClick={handleExtract}
              disabled={pastedText.trim() === "" && !selectedFile}
            >
              Extract
            </button>
          )}
          {step === "review" && (
            <>
              {confirmStatus && (
                <span style={{ fontSize: "0.85rem", color: "#666", marginRight: "0.75rem" }}>{confirmStatus}</span>
              )}
              <button className="btn btn-primary btn-sm" onClick={handleConfirm} disabled={!!confirmStatus}>
                Add to Guide
              </button>
            </>
          )}
          {step === "offer" && (
            <>
              <button className="btn btn-secondary btn-sm" onClick={handleClose}>
                No thanks
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => {
                  onOfferTripAI(lastCreatedEvents);
                  handleClose();
                }}
              >
                Yes, generate trip
              </button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      <MultiSelectModal
        show={countryPickerShow}
        onHide={() => setCountryPickerShow(false)}
        MultiselectItems={countryPickerItems}
        MultiselectSelectedItems={[]}
        MultiselectMode="uni"
        MultiselectCallback={handleCountryPicked}
        MultiselectTitle="Select Country"
        MultiselectSearchText=""
      />

      <MultiSelectModal
        show={cityPickerShow}
        onHide={() => setCityPickerShow(false)}
        MultiselectItems={cityPickerTarget !== null ? cityPickerItemsFor(places[cityPickerTarget].country) : []}
        MultiselectSelectedItems={[]}
        MultiselectMode="uni"
        MultiselectCallback={handleCityPicked}
        MultiselectTitle={cityPickerTarget !== null ? `Select a city in ${places[cityPickerTarget].country}` : "Select City"}
        MultiselectSearchText=""
      />

      {cityPromptShow && (
        <PromptModal
          show={true}
          onHide={() => { setCityPromptShow(false); setCityPromptTarget(null); }}
          PromptModalTitle="City"
          PromptModalMessage="Enter City"
          PromptModalInputs={[{ name: "City", value: cityPromptTarget !== null ? (places[cityPromptTarget].city || "") : "" }]}
          PromptModalButtons={[
            { Title: "OK", Method: handleCityTyped },
            { Title: "CANCEL", Method: null },
          ]}
        />
      )}
    </>
  );
}
